/**
 * <HonestyRail variant /> — CD R11 Finding #1 (biggest consistency win).
 *
 * Before: 6 Phase 11+ surfaces each hand-rolled a "caveat banner" with
 * different colours, icons, weights, capitalisation, and placements. The
 * honesty rail is the product's core credibility signal — it must not
 * look improvised, and it must read identically everywhere so operators
 * learn it once.
 *
 * Three semantic variants mapped to existing tokens:
 *
 *   limitation  (amber)  — "delay ≠ prevent", "not a substitute for X",
 *                          "reference snapshot, not live feed"
 *   synthetic   (purple) — "modelled association, not causation",
 *                          "scenario projection — pair with ensemble"
 *   provenance  (green)  — "all values source-attributed",
 *                          "gap-classes flagged where data is absent"
 *
 * Visual anatomy (CD R11 jsx):
 *   3px left-border rail · mono tag (icon + word) · body prose
 *
 * The variant ICON+LABEL combo is deliberately constrained — the same
 * three appear everywhere so operators pattern-match instantly. A custom
 * `label` overrides the variant's default label for cases like
 * "DELAY ≠ PREVENT" on the Travel Restrictions surface, but the variant
 * controls the visual semantic (which colour, which icon).
 */

import type { ReactNode } from 'react';

export type HonestyRailVariant = 'limitation' | 'synthetic' | 'provenance';

interface HonestyRailProps {
  variant: HonestyRailVariant;
  /** Optional override for the tag word. Variant still controls icon+colour.
   *  Use for surface-specific framings like "DELAY ≠ PREVENT" or
   *  "NOT A SUBSTITUTE". Defaults to the variant's canonical label. */
  label?: string;
  children: ReactNode;
}

const VARIANT_META: Record<HonestyRailVariant, { color: string; bg: string; icon: string; label: string }> = {
  limitation: {
    color: 'var(--bm-threat-3)',
    bg:    'var(--bm-threat-3-bg)',
    icon:  '⚠',
    label: 'LIMITATION',
  },
  synthetic: {
    color: 'var(--bm-prov-synthetic)',
    bg:    'var(--bm-prov-synthetic-bg)',
    icon:  '◇',
    label: 'MODELLED',
  },
  provenance: {
    color: 'var(--bm-prov-confirmed)',
    bg:    'var(--bm-prov-confirmed-bg)',
    icon:  '✓',
    label: 'PROVENANCE',
  },
};

export function HonestyRail({ variant, label, children }: HonestyRailProps) {
  const v = VARIANT_META[variant];
  return (
    <div
      role="note"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        margin: 'var(--bm-space-3) 0',
        padding: '10px 14px',
        background: v.bg,
        borderLeft: `3px solid ${v.color}`,
        borderRadius: 'var(--bm-radius-sm)',
        fontSize: 12.5, lineHeight: 1.55,
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontFamily: 'var(--bm-font-mono)',
          fontSize: 10.5, fontWeight: 700,
          letterSpacing: '.5px',
          color: v.color,
          textTransform: 'uppercase' as const,
          whiteSpace: 'nowrap' as const,
        }}
      >
        {v.icon} {label ?? v.label}
      </span>
      <span style={{ color: 'var(--bm-text)' }}>{children}</span>
    </div>
  );
}
